import { useEffect, useState, useCallback } from 'react';
import Header from '../../components/Header/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import CategoryTable from '../../components/Table/CategoryTable/CategoryTable';
import Fuse from 'fuse.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deleteExperienceDetails, getAllInterviewExperiences, getAllDraftInterviewExperiences, deleteDraftExperienceDetails, moveDraftToPublished, updateDraftExperienceStatus } from '../../services/operations/interviewExperiences';

const environmentTabs = [
    { label: 'DEV', value: 'dev' },
    { label: 'PROD', value: 'prod' }
];

const AllInterviewExperiences = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { AllInterviewExperiences, AllDraftInterviewExperiences } = useSelector((state) => state.interviewExperience);
    const [modalOpen, setModalOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredExperiences, setFilteredExperiences] = useState([]);
    const [fuse, setFuse] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        return searchParams.get('tab') || 'published';
    });
    const [currentEnv, setCurrentEnv] = useState(() => {
        const isDev = localStorage.getItem('isDev') === 'true';
        return isDev ? 'dev' : 'prod';
    });
    const [showTooltip, setShowTooltip] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isMovingToPublished, setIsMovingToPublished] = useState(false);
    const navigate = useNavigate();

    const dispatch = useDispatch();

    useEffect(() => {
        const currentTab = searchParams.get('tab');
        if (currentTab !== activeTab) {
            setSearchParams({ tab: activeTab });
        }
    }, [activeTab, setSearchParams, searchParams]);

    const initializeFuse = (experiences) => {
        const options = {
            keys: ['title'],
            threshold: 0.1,
            includeScore: true,
        };
        return new Fuse(experiences, options);
    };

    // Get current experiences based on active tab
    const getCurrentExperiences = useCallback(() => {
        return activeTab === 'published' ? AllInterviewExperiences : AllDraftInterviewExperiences || [];
    }, [activeTab, AllInterviewExperiences, AllDraftInterviewExperiences]);

    useEffect(() => {
        localStorage.setItem('isDev', currentEnv === 'dev');
        dispatch(getAllInterviewExperiences());
        dispatch(getAllDraftInterviewExperiences());
    }, [dispatch, currentEnv]);

    useEffect(() => {
        const currentExperiences = getCurrentExperiences();
        if (currentExperiences.length !== 0) {
            setFilteredExperiences(currentExperiences);
            setFuse(initializeFuse(currentExperiences));
        } else {
            setFilteredExperiences([]);
            setFuse(null);
        }
        setSearchTerm('');
    }, [AllInterviewExperiences, AllDraftInterviewExperiences, activeTab, getCurrentExperiences]);

    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        const currentExperiences = getCurrentExperiences();

        if (term.trim() === '') {
            setFilteredExperiences(currentExperiences);
            return;
        }

        if (fuse) {
            const results = fuse.search(term);
            const filtered = results.map((result) => result.item);
            setFilteredExperiences(filtered);
        }
    };

    const toggleSearch = () => {
        setSearchOpen(!searchOpen);
        if (searchOpen) {
            setSearchTerm('');
            setFilteredExperiences(getCurrentExperiences());
        }
    };

    const handleExperienceEdit = (e, prop) => {
        e.stopPropagation();
        if (activeTab === 'published') {
            navigate(`${prop.slug}`);
        } else {
            navigate(`draft/${prop.slug || prop.id}`);
        }
    };

    const handleDeleteExperience = async (id, setDeleteModalOpen) => {
        setIsDeleting(true);
        try {
            if (activeTab === 'published') {
                dispatch(deleteExperienceDetails(id));
            } else {
                dispatch(deleteDraftExperienceDetails(id));
            }
        } catch (error) {
            console.error('Error deleting experience:', error);
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
        }
    };

    const handleMoveDraftToPublished = (id) => {
        setIsMovingToPublished(true);
        try {
            dispatch(moveDraftToPublished(id));
        } catch (error) {
            console.error('Error moving draft to published:', error);
        } finally {
            setIsMovingToPublished(false);
        }
    };

    const handleStatusUpdate = (id, status) => {
        dispatch(updateDraftExperienceStatus(id, status));
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchOpen(false);
    };

    const handleEnvChange = (env) => {
        setCurrentEnv(env);
    };

    return (
        <div className='flex dark:bg-dark font-primary h-screen overflow-hidden bg-[#fafafa]'>
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            <div className='relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden'>
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <main>
                    <div className='px-4 mb-8 sm:px-6 lg:px-8 w-full max-w-9xl mx-auto'>
                        <div className='flex justify-between items-center px-6 py-4'>
                            <h2 className='text-2xl font-bold text-[#212121] dark:text-zinc-200'>
                                All Interview Experiences
                            </h2>
                            <div className='flex items-center gap-2'>
                                <div className='relative'>
                                    <button 
                                        className='bg-[#FFD3B5] dark:bg-zinc-800 text-[#212121] dark:text-zinc-200 font-semibold py-2 px-4 rounded-lg hover:bg-[#FFB885] dark:hover:bg-zinc-700 transition-all duration-200 flex items-center gap-2'
                                        onMouseEnter={() => setShowTooltip(true)}
                                        onMouseLeave={() => setShowTooltip(false)}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        How It Works
                                    </button>
                                    
                                    {showTooltip && (
                                       <div className='absolute top-full mt-2 right-0 w-80 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-lg p-4 z-50'>
                                       <div className='text-sm text-gray-700 dark:text-zinc-300'>
                                         <h3 className='font-semibold text-[#212121] dark:text-zinc-200 mb-2'>Interview Experience Workflow</h3>
                                         <ol className='space-y-2 list-decimal list-inside'>
                                           <li>
                                             <span className='font-medium'>User Submission:</span> When a user submits an interview experience, it appears under the <span className='text-blue-600 dark:text-blue-400 font-medium'>Draft</span> tab with a <span className='text-yellow-600 dark:text-yellow-400 font-medium'>Pending</span> status.
                                           </li>
                                           <li>
                                             <span className='font-medium'>Admin Review:</span> Admins review the submission and may provide comments or suggestions for improvement.
                                           </li>
                                           <li>
                                             <span className='font-medium'>Publication:</span> Once all feedback is addressed, the experience is moved to the <span className='text-green-600 dark:text-green-400 font-medium'>Published</span> section.
                                           </li>
                                           <li>
                                             <span className='font-medium'>Rejection:</span> Submissions that are inappropriate or have unresolved issues remain in the <span className='text-red-600 dark:text-red-400 font-medium'>Unapproved</span> section.
                                           </li>
                                         </ol>
                                       </div>
                                       <div className='absolute -top-2 right-4 w-4 h-4 bg-white dark:bg-zinc-900 border-l border-t border-gray-200 dark:border-zinc-700 transform rotate-45'></div>
                                     </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Environment Tabs */}
                        <div className='flex justify-center my-6 bg-zinc-900 rounded-full text-sm w-5/12 mx-auto'>
                            <TabSwitcher
                                tabs={environmentTabs}
                                activeTab={currentEnv}
                                onChange={handleEnvChange}
                            />
                        </div>

                        <p className='text-center text-8xl font-bold mt-4 text-zinc-700'>{currentEnv.toUpperCase()}</p>

                        {/* Tab Navigation */}
                        <div className='flex justify-center mb-6'>
                            <div className='flex bg-gray-100 dark:bg-zinc-800 rounded-lg p-1'>
                                <button
                                    onClick={() => handleTabChange('published')}
                                    className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                                        activeTab === 'published'
                                            ? 'bg-white dark:bg-zinc-700 text-[#212121] dark:text-zinc-200 shadow-sm'
                                            : 'text-gray-600 dark:text-zinc-400 hover:text-[#212121] dark:hover:text-zinc-200'
                                    }`}
                                >
                                    Published Experiences ({AllInterviewExperiences?.length || 0})
                                </button>
                                <button
                                    onClick={() => handleTabChange('draft')}
                                    className={`px-6 py-2 rounded-md font-medium transition-all duration-200 ${
                                        activeTab === 'draft'
                                            ? 'bg-white dark:bg-zinc-700 text-[#212121] dark:text-zinc-200 shadow-sm'
                                            : 'text-gray-600 dark:text-zinc-400 hover:text-[#212121] dark:hover:text-zinc-200'
                                    }`}
                                >
                                    Draft Experiences ({AllDraftInterviewExperiences?.length || 0})
                                </button>
                            </div>
                        </div>

                        <div className='flex justify-between items-center px-6 py-4'>
                            <a href='https://dev.takeuforward.org/interview/share-experience' target='_blank' rel='noopener noreferrer'
                                className='bg-[#FFD3B5] dark:bg-zinc-800 text-[#212121] dark:text-zinc-200 font-semibold py-2 px-4 rounded-lg'
                            >
                                Add Interview Experiences
                            </a>
                            <button
                                onClick={toggleSearch}
                                className='bg-[#FFD3B5] dark:bg-zinc-800 text-[#212121] dark:text-zinc-200 font-semibold py-2 px-4 rounded-lg w-44'
                            >
                                {searchOpen ? 'Close Search' : 'Search'}
                            </button>
                        </div>
                        {searchOpen && (
                            <div className='flex justify-center items-center mb-4'>
                                <input
                                    autoFocus
                                    type='text'
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    placeholder={`Search ${activeTab} experiences...`}
                                    className='w-full max-w-2xl px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                                />
                            </div>
                        )}

                        <div className='flex gap-y-2 flex-col justify-start'>
                            {filteredExperiences.length > 0 ? (
                                <div className='flex flex-col p-4'>
                                    <CategoryTable
                                        page={activeTab === 'published' ? 'interview' : 'interview-draft'}
                                        topics={filteredExperiences}
                                        setModalOpen={setModalOpen}
                                        modalOpen={modalOpen}
                                        handleDelete={handleDeleteExperience}
                                        handleEdit={handleExperienceEdit}
                                        handleMoveDraftToPublished={handleMoveDraftToPublished}
                                        handleStatusUpdate={handleStatusUpdate}
                                        isDeleting={isDeleting}
                                        isMovingToPublished={isMovingToPublished}
                                    />
                                </div>
                            ) : (
                                <div className='flex justify-center items-center text-4xl dark:text-zinc-300'>
                                    No {activeTab === 'published' ? 'Published' : 'Draft'} Interview Experiences to show
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AllInterviewExperiences;

const TabSwitcher = ({ tabs, activeTab, onChange, size = "md" }) => {
  const sizeClasses = {
    sm: "text-sm py-1.5",
    md: "text-base py-2",
    lg: "text-lg py-2.5"
  };

  return (
    <div className="flex justify-center bg-zinc-900 rounded-full mx-auto w-full">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => onChange(tab.value)}
          className={`
            relative w-full px-4 rounded-full  transition-colors duration-200
            ${sizeClasses[size]}
            ${activeTab === tab.value ? 'text-white' : 'text-zinc-500 hover:text-zinc-400'}
          `}
        >
          {activeTab === tab.value && (
            <div className="absolute inset-0 w-full border-2 border-zinc-700 rounded-full" />
          )}
          <span className="relative z-10 w-full">{tab.label}</span>
        </button>
      ))}
    </div>
  );
};
